import { Check } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '../../../../components/EmptyState';
import { Skeleton } from '../../../../components/Skeleton';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import type { IssuedAssetLine } from '../issuanceTypes';
import { ISSUANCE_STATUS_LABEL, PROPERTY_CLASSIFICATION_LABEL, isCustody } from '../issuanceTypes';

/**
 * What the article IS, as anybody would write it — manufacturer, brand and model — falling back
 * to the governed name where the item carries none of them.
 *
 * ONE helper for the cell, the search and the sort, so the three cannot disagree about which
 * value leads: a title the search cannot reach is a defect.
 */
function identityOf(row: IssuedAssetLine): string {
  const parts = [row.manufacturer, row.brandName, row.model]
    .map((p) => p?.trim())
    .filter((p): p is string => Boolean(p));

  return parts.length > 0 ? parts.join(' ') : row.itemName;
}

const outstandingOf = (row: IssuedAssetLine) => row.quantity - row.quantityReturned;

/**
 * Issued Assets — every article issued to this employee: each line of a slip naming them as
 * ACCOUNTABLE officer, or as RECIPIENT where the slip names no officer — the reference's own
 * selection, so a dispense handed to them is listed beside what they signed for.
 *
 * **Drawn on TWO surfaces, and it lives in Logistics because both show Logistics records** — the
 * personnel profile's Issued Assets panel, gated on `issuances.read` (the key of what it shows,
 * never the personnel page it sits on), and the Employee Assets profile, gated on
 * `employee-assets.read`. Each surface fetches its own rows and gates its own entry; this
 * component only renders them.
 *
 * **One row per ARTICLE, not per slip.** The question is what property this person is
 * accountable for, and one slip can carry a laptop and a projector. The slip is a column, and the
 * anchor opens it: an issued line has no profile of its own, so the record it belongs to is
 * where the full trail — the lots, every return, the signatories — is read.
 *
 * **Returned lines stay**, with an outstanding count of zero, because a history of what someone
 * once signed for is what an accountability record is for. Show deleted lifts deleted slips.
 *
 * **Searched, sorted and paged in memory** — the 201 file's exemption: the list arrives whole,
 * so the filter sees everything there is.
 *
 * **No checkbox column, and that is the module's shape rather than an unbuilt bulk action.**
 * Every write here belongs to Logistics and is made one slip at a time — a return is recorded
 * against one line of one issuance, and there is no endpoint that returns across slips — so a
 * selection could never lead anywhere.
 */
export function IssuedAssetsPanel({
  id,
  fill = false,
  rows,
  failed,
  employeeIsActive,
  onIssue,
  onReturn,
}: {
  /** The panel's DOM id — `<module>-issued-assets`, so the two surfaces never share one. */
  id: string;
  /**
   * A full-height profile TAB rather than a sidebar section: the table takes the height it is
   * given, so the pager sits on the panel's bottom edge, inside the `p-4` inset a `bleed`
   * panel owes its child. Off, it renders in a `<ProfileSection>`, which names itself from the
   * sidebar nav.
   */
  fill?: boolean;
  /** Null while the first fetch is in flight. */
  rows: IssuedAssetLine[] | null;
  /** The fetch failed — a message to state rather than a list that reads "nothing issued". */
  failed: string | null;
  employeeIsActive: boolean;
  /**
   * Opens the Issue form for this employee. OMITTED on the Employee Assets profile, which is a
   * history to read rather than a place to release stock (product call, 08 Oct 2026) — and an
   * absent callback draws no Create at all, rather than a disabled one.
   */
  onIssue?: () => void;
  onReturn: (row: IssuedAssetLine) => void;
}) {
  const navigate = useNavigate();
  const [showDeleted, setShowDeleted] = useState(false);

  const visible = useMemo(
    () => (rows === null ? [] : showDeleted ? rows : rows.filter((x) => x.isActive)),
    [rows, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? visible.filter((x) =>
            [identityOf(x), x.itemName, x.sku, x.serialNumber, x.inventoryNumber, x.issuanceNumber]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : visible;

      /*
        `field:direction` — what `useDataTable.toggleSort` writes and `<DataTable>` reads back.
        NOT the leading `-` the server-side `ListQuery` uses: parsed that way the first order is
        right and every click after it silently falls back to the server's order.
      */
      const [field, dir] = (query.sort ?? '').split(':');
      const direction = dir === 'desc' ? -1 : 1;

      const pick = (row: IssuedAssetLine): string | number =>
        field === 'item'
          ? identityOf(row)
          : field === 'sku'
            ? row.sku
            : field === 'issuanceNumber'
              ? row.issuanceNumber
              : field === 'outstanding'
                ? outstandingOf(row)
                : field === 'quantity'
                  ? row.quantity
                  : '';

      // A COPY: with no search term `matched` IS the visible array, and sorting in place would
      // reorder the panel's own state behind the table.
      const sorted =
        field === ''
          ? matched
          : [...matched].sort((a, b) => {
              const av = pick(a);
              const bv = pick(b);
              return (
                (typeof av === 'number' && typeof bv === 'number'
                  ? av - bv
                  : String(av).localeCompare(String(bv))) * direction
              );
            });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [visible],
  );

  // Newest slip first is the server's order, so no default sort is set: an unsorted table reads
  // exactly what arrived, which is the order the profile was given.
  const state = useDataTable<IssuedAssetLine>({ fetchPage, pageSize: 10, enabled: rows !== null });

  const columns: DataColumn<IssuedAssetLine>[] = [
    {
      /*
        The ANCHOR is the article — its make and model over the governed name. Two laptops are
        two rows reading "Laptop" by the name alone, which titles every row alike. Legal here:
        a `<DataTablePanel>` provides no index-table surface, so the stacked-cell rule's dev
        error does not reach it — the call the goods-receipt lines take.
      */
      key: 'item',
      header: 'Item',
      width: 260,
      sortable: true,
      render: (x) => {
        const identity = identityOf(x);
        return (
          <StackedCell primary={identity} secondary={identity === x.itemName ? undefined : x.itemName} />
        );
      },
    },
    {
      key: 'sku',
      header: 'SKU',
      width: 130,
      sortable: true,
      render: (x) => <TextCell value={x.sku} mono />,
    },
    {
      key: 'serialNumber',
      header: 'Serial no.',
      width: 150,
      summary: true,
      // Null on a quantity line — counted stock has no article to name.
      render: (x) => <TextCell value={x.serialNumber ?? null} mono />,
    },
    {
      key: 'inventoryNumber',
      header: 'Property no.',
      width: 160,
      render: (x) => <TextCell value={x.inventoryNumber ?? null} mono />,
    },
    {
      key: 'issuanceNumber',
      header: 'Slip',
      width: 140,
      sortable: true,
      summary: true,
      render: (x) => <TextCell value={x.issuanceNumber} mono />,
    },
    {
      key: 'issuanceType',
      header: 'Method',
      width: 100,
      summary: true,
      render: (x) => <TextCell value={x.issuanceTypeLabel} />,
    },
    {
      key: 'acknowledgementType',
      header: 'Form',
      width: 90,
      render: (x) => (
        <TextCell
          value={x.acknowledgementType == null ? null : PROPERTY_CLASSIFICATION_LABEL[x.acknowledgementType]}
          mono
        />
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      width: 80,
      align: 'right',
      sortable: true,
      // The UNIT beside the count — the reference's own Qty cell — or 3 reads the same for boxes
      // and laptops.
      render: (x) => (
        <TextCell value={`${x.quantity} ${x.unitOfMeasureCode ?? ''}`.trim()} mono numeric />
      ),
    },
    {
      key: 'quantityReturned',
      header: 'Returned',
      width: 100,
      align: 'right',
      render: (x) => <TextCell value={String(x.quantityReturned)} mono numeric />,
    },
    {
      // What the employee is still holding — the figure an accountability record is read for.
      key: 'outstanding',
      header: 'Outstanding',
      width: 120,
      align: 'right',
      sortable: true,
      render: (x) => <TextCell value={String(outstandingOf(x))} mono numeric />,
    },
    {
      key: 'expectedReturnDate',
      header: 'Due back',
      width: 130,
      // A calendar date, so `formatDateOnly` through a mono `<TextCell>`, never `<DateCell>`.
      render: (x) => (
        <TextCell value={x.expectedReturnDate == null ? null : formatDateOnly(x.expectedReturnDate)} mono />
      ),
    },
    {
      /*
        The SLIP's own lifecycle, beside the row-state pill the shell hoists to the end — the
        issuance register's own pair. Overdue is a stage a reader is scanning for, so it takes
        the one tone that says something is wrong; every other stage is the slate pill the
        register draws.
      */
      key: 'stage',
      header: 'Stage',
      width: 130,
      stage: true,
      render: (x) =>
        x.isOverdue ? (
          <StatusText tone="blocked">Overdue</StatusText>
        ) : (
          <StatusText tone="inactive">{ISSUANCE_STATUS_LABEL[x.status]}</StatusText>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  // ONE frame for all three states, so a tab does not jump between its inset and a section's.
  const frame = (content: ReactNode) =>
    fill ? (
      <div className="flex h-full min-h-0 min-w-0 flex-col p-4">{content}</div>
    ) : (
      <ProfileSection>{content}</ProfileSection>
    );

  if (failed !== null && rows === null) {
    return frame(<EmptyState message={failed} />);
  }

  if (rows === null) {
    return frame(<Skeleton className="h-64 w-full" />);
  }

  return frame(
    <DataTablePanel<IssuedAssetLine>
      id={id}
      fill={fill}
      state={state}
      columns={columns}
      rowKey={(x) => x.id}
      searchPlaceholder="Search item, SKU, serial, property no. or slip"
      /*
        Create opens the Issue stock form with THIS employee as the accountable officer. The
        key is the one the form writes under — `issuances.create` — never a personnel key.
      */
      onCreate={onIssue}
      createLabel="Issue asset"
      createPermission="issuances.create"
      createDisabledReason={
        employeeIsActive ? null : 'This employee is deleted. Restore them before issuing property to them.'
      }
      // The anchor opens the SLIP — an issued line has no page of its own.
      onRowOpen={(x) => navigate(`/inventory/issuances/${x.issuanceId}`)}
      openPermission="issuances.read"
      selectable={false}
      /*
        Return is drawn only where it APPLIES — a live custody line with something still out.
        A settled line, a deleted slip and a dispense have nothing to take back, so the entry
        is absent rather than disabled.
      */
      rowActions={(x) =>
        x.isActive && isCustody(x.issuanceType) && outstandingOf(x) > 0
          ? [
              {
                key: `return-${x.id}`,
                label: 'Record return',
                permission: 'issuances.return',
                onClick: () => onReturn(x),
              },
            ]
          : []
      }
      otherInfo={(x) => ({
        createdAt: x.createdAt,
        createdByName: x.createdByName,
        updatedAt: x.updatedAt,
        updatedByName: x.updatedByName,
      })}
      settingsItems={[
        {
          key: 'show-deleted',
          label: 'Show deleted',
          icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
          onClick: () => setShowDeleted((current) => !current),
        },
      ]}
      emptyMessage="Nothing has been issued to this employee."
      emptySearchMessage="No issued asset matches that search."
    />,
  );
}
